import _ from 'lodash'
import { FTProcess } from '@/models/FTProcess';
import { weightToPieces, shapeArea, initProcess, ProcessConfig, ConversionResults, ParsedVariables } from './index'
import { FTProcessRow } from '@/models/FTProcessRow';
import { ConversionDetails } from '@/models/ConversionDetails';

export function loadConfig({ processRow }: ParsedVariables): ProcessConfig {
  return {
    inputs: {
      goods: [
        {
          goodsType: '',
          goodsFilter: {
            form: ['sheet', 'die-cut-sheet']
          },
          multiple: true,
          addMultipleButtonText: 'add more input goods',
          params: [
            {
              name: 'Width',
              type: 'label',
              value: _.get(processRow, 'goodsObject.dimensions.width', ''),
              unit: 'mm',
              disabled: true
            },{
              name: 'Length',
              type: 'label',
              value: _.get(processRow, 'goodsObject.dimensions.length', ''),
              unit: 'mm',
              disabled: true
            },
            {
              name: 'Unit Weight',
              type: 'label',
              value: _.get(processRow, 'goodsObject.unitWeight', ''),
              unit: 'kg',
              disablePrint: true,
              disabled: true
            },
            {
              name: 'PCs/sheet',
              type: 'text',
              value: 'pcsPerSheet',
              disablePrint: true,
              disabled: false
            },
            {
              name: 'Input Quantity',
              type: 'quantity',
              value: 'quantity',
              disabled: false
            }
          ]
        }
      ]
    },
    outputs: {
      type: 'single',
      arguments: [
        {
          name: 'Shape',
          type: 'shape',
          value: 'shape',
          optionsArray: [ 'Round', 'Oval', 'Rectangle', 'Rectangle+Oval', 'Other'],
          disabled: false
        },
        {
          name: 'Area',
          type: 'dimension',
          value: 'area',
          unit: 'sq. mm',
          showIf: _.get(processRow, 'dimensions.shape') === 'Other',
          disabled: false
        },
        {
          name: 'Diameter',
          type: 'dimension',
          value: 'diameter',
          unit: 'mm',
          showIf: _.get(processRow, 'dimensions.shape') === 'Round',
          disabled: false
        },
        {
          name: 'Width',
          type: 'dimension',
          value: 'width',
          unit: 'mm',
          showIf: ['Oval', 'Rectangle', 'Rectangle+Oval'].includes(_.get(processRow, 'dimensions.shape')),
          disabled: false
        },
        {
          name: 'Length',
          type: 'dimension',
          value: 'length',
          unit: 'mm',
          showIf: ['Oval', 'Rectangle', 'Rectangle+Oval'].includes(_.get(processRow, 'dimensions.shape')),
          disabled: false
        }
      ]
    },
    tableHeaders: [
      {
        text: 'Nominal Speed',
        value: 'nominalSpeed'
      },
      {
        text: 'Piece(s) per stroke',
        value: 'pcsPerStroke'
      },
      {
        text: 'Planned Hours(s)',
        value: 'plannedHours'
      },
      {
        text: 'No of Pieces',
        value: 'numberOfPieces'
      },
      {
        text: 'Expected Output Waste',
        value: 'wastePercentage'
      },
      {
        text: 'Expected Output Waste Weight',
        value: 'waste'
      },
      {
        text: 'Expected Output Net Weight',
        value: 'netWeight'
      }
    ]
  }
}

export function convert(process: FTProcess) : ConversionResults {
  const conversionResult : ConversionResults = {
    inputs: [],
    outputs: [],
    details: new ConversionDetails(),
  };

  initProcess(process);

  let totalNoOfSheets = 0, piecesEstimate = 0, totalAreaSqm = 0, inputQuantity = 0;
  conversionResult.details.totalQuantity = 0;
  conversionResult.outputs = process.outputs;
  const outputFTProcessRow = process.outputs[0];
  outputFTProcessRow.quantity = 0;
  outputFTProcessRow.goodsObject.copyFrom(process.inputs[0].goodsObject);
  outputFTProcessRow.goodsObject.form = 'die-cut-sheet';
  if (outputFTProcessRow.dimensions) {
    outputFTProcessRow.goodsObject.dimensions = outputFTProcessRow.dimensions;
  }
  _.map(process.inputs, (processInputRow: FTProcessRow) => {
    inputQuantity += processInputRow.quantity;
    if (outputFTProcessRow.goodsObject.dimensions && processInputRow.pcsPerSheet && processInputRow.goodsObject.dimensions) {
      const numberofSheets = weightToPieces(
          processInputRow.quantity, processInputRow.goodsObject.density,
          shapeArea({ shape: processInputRow.goodsObject.dimensions.shape, params: processInputRow.goodsObject.dimensions }));
      const noOfPieces = processInputRow.pcsPerSheet * numberofSheets;
      const dieShapeArea = shapeArea({
        shape: outputFTProcessRow.dimensions.shape,
        params: outputFTProcessRow.dimensions,
        outUnit: 'm'
      });
      const unitWeight = (processInputRow.goodsObject.density * dieShapeArea) / 1000;
      piecesEstimate += noOfPieces;
      outputFTProcessRow.quantity += noOfPieces * unitWeight;
      totalNoOfSheets += numberofSheets;
      totalAreaSqm += dieShapeArea * noOfPieces;
    }
  })
  if(outputFTProcessRow.quantity && inputQuantity) {
    const baseUnit = _.get(process, 'ftFactoryTicket.order.product.packingMode.0.unit1');
    if(baseUnit == 'kg') {
      outputFTProcessRow.goodsObject.setPerKgRatio('plA', 1);
    } else {
      outputFTProcessRow.goodsObject.setPerKgRatio('plA', piecesEstimate/outputFTProcessRow.quantity);
    }
    conversionResult.details.netWeight = outputFTProcessRow.quantity;
    outputFTProcessRow.goodsObject.unitWeight = piecesEstimate ? outputFTProcessRow.quantity/piecesEstimate: 0;
    outputFTProcessRow.goodsObject.density = _.round((outputFTProcessRow.quantity*1000)/totalAreaSqm, 2);
    conversionResult.details.waste = inputQuantity - outputFTProcessRow.quantity;
    conversionResult.details.wastePercentage = (conversionResult.details.waste/inputQuantity) * 100;
  }

  conversionResult.details.numberOfPieces = piecesEstimate;
  process.machineDetail.pcsPerStroke = totalNoOfSheets ? Math.floor(piecesEstimate / totalNoOfSheets) : 0;
  if (process.machineDetail.pcsPerStroke && conversionResult.outputs[0].goodsObject.unitWeight) {
      conversionResult.details.kgPerHour = 60 * conversionResult.outputs[0].goodsObject.unitWeight * process.machineDetail.mechanicalSpeed * process.machineDetail.pcsPerStroke;
      conversionResult.details.plannedHours = process.inputs[0].quantity / conversionResult.details.kgPerHour;
  }
  conversionResult.details.pcsPerStroke = process.machineDetail.pcsPerStroke;

  return conversionResult;
}